import * as React from 'react'
import * as ReactDOM from 'react-dom'
import moment from 'moment'
import { useParams } from 'react-router-dom'
import { usePermissionChecker } from '@/modules/shared/hooks/usePermissionChecker'
import AddCountryCodeModal from '../modals/AddCountryCodeModal'
import { ActivityModel } from '@/modules/drivers/types/activities'
import { ActivityType } from '@/app/constants/activity'
import {
  getActivityType,
  getActivityTypeNumber,
} from '@/modules/drivers/helpers/ActivityTypeResolver'

export enum Operations {
  Add = 'Add',
  Edit = 'Edit',
  AddCountryCode = 'AddCountryCode',
  FerryCrossing = 'FerryCrossing',
  OutOfScope = 'OutOfScope',
  Crew = 'Crew',
  GenerateAttestation = 'GenerateAttestation',
}

type MenuData = {
  x: number
  y: number
  activity: ActivityModel
  canEdit: boolean
  canGenerateAttestation: boolean
  translate: (text: string) => any
  onAdd?: (activity: ActivityModel) => void
  onEdit?: (activity: ActivityModel, operation: string) => void
  onContextMenuChanged: (value: boolean, activity: any) => void
  addStartTime: any
  hideAdd?: boolean

  addOnly?: boolean
}

type ActivityContextMenuContextType = {
  openMenu: (data: MenuData) => void
  closeMenu: () => void
  isOpen: boolean
}

const ActivityContextMenuContext =
  React.createContext<ActivityContextMenuContextType>({
    openMenu: () => {},
    closeMenu: () => {},
    isOpen: false,
  })

export const useActivityContextMenu = () =>
  React.useContext(ActivityContextMenuContext)

export const ActivityContextMenuProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const { companyId, driverId } = useParams<{
    companyId: string
    driverId: string
  }>()
  const can = usePermissionChecker(companyId)
  const canAddCountryCode = Boolean(
    companyId && driverId && can('canEditActivities'),
  )
  const [countryCodeDateTime, setCountryCodeDateTime] = React.useState<
    string | null
  >(null)
  const [menuData, setMenuData] = React.useState<MenuData | null>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)
  const visitedRef = React.useRef(false)

  const openMenu = React.useCallback((data: MenuData) => {
    visitedRef.current = false
    setMenuData(data)
  }, [])

  const closeMenu = React.useCallback(() => {
    setMenuData((prev) => {
      if (prev) {
        prev.onContextMenuChanged(false, null)
      }
      return null
    })
  }, [])

  React.useEffect(() => {
    if (!menuData) return undefined

    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeMenu()
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMenu()
      }
    }

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick)
      document.addEventListener('keydown', handleKeyDown)
    }, 0)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuData, closeMenu])

  React.useEffect(() => {
    if (!menuData || !menuRef.current) return undefined
    const rect = menuRef.current.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    let adjusted = false
    let newX = menuData.x
    let newY = menuData.y

    if (rect.right > vw) {
      newX = vw - rect.width - 5
      adjusted = true
    }
    if (rect.bottom > vh) {
      newY = vh - rect.height - 5
      adjusted = true
    }
    if (adjusted) {
      setMenuData((prev) => (prev ? { ...prev, x: newX, y: newY } : null))
    }
  }, [menuData?.x, menuData?.y])

  const handleItemClick = React.useCallback(
    (action: Operations) => {
      if (!menuData) return
      visitedRef.current = true

      const { activity, onAdd, onEdit, addStartTime } = menuData

      if (action === Operations.Add) {
        const activityToAdd = {
          ...activity,
          ...(menuData.addOnly ? { isVehicleSource: false } : {}),
          type: getActivityTypeNumber(ActivityType.WORK),
          startTime: addStartTime,
          endTime: addStartTime && addStartTime.clone().add(15, 'minutes'),
        }
        onAdd && onAdd(activityToAdd as any)
        menuData.onContextMenuChanged(false, activityToAdd)
      } else if (action === Operations.AddCountryCode) {
        if (!menuData.canEdit || !canAddCountryCode) return
        setCountryCodeDateTime(
          moment(addStartTime ?? activity.startTime).format('YYYY-MM-DD HH:mm'),
        )
        menuData.onContextMenuChanged(false, null)
      } else if (action === Operations.Edit) {
        onEdit && onEdit(activity, action)
        menuData.onContextMenuChanged(false, activity)
      } else if (action === Operations.GenerateAttestation) {
        onEdit && onEdit(activity, action)
        menuData.onContextMenuChanged(false, activity)
      } else {
        let editedActivity = { ...activity }

        if (action === Operations.FerryCrossing) {
          editedActivity = {
            ...editedActivity,
            isFerryCrossing: !editedActivity.isFerryCrossing,
          }
        } else if (action === Operations.Crew) {
          editedActivity = {
            ...editedActivity,
            isCrew: !editedActivity.isCrew,
          }
        } else if (action === Operations.OutOfScope) {
          editedActivity = {
            ...editedActivity,
            isOutOfScope: !editedActivity.isOutOfScope,
          }
        }

        onEdit && onEdit(editedActivity as any, action)
        menuData.onContextMenuChanged(false, editedActivity)
      }

      setMenuData(null)
    },
    [menuData, canAddCountryCode],
  )

  const isOpen = menuData !== null

  const contextValue = React.useMemo(
    () => ({ openMenu, closeMenu, isOpen }),
    [openMenu, closeMenu, isOpen],
  )

  return (
    <ActivityContextMenuContext.Provider value={contextValue}>
      {children}
      {menuData &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="react-contextmenu react-contextmenu--visible"
            style={{
              position: 'fixed',
              top: menuData.y,
              left: menuData.x,
              zIndex: 99999,
              background: '#fff',
              borderRadius: 4,
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              padding: '4px 0',
              minWidth: 180,
            }}
          >
            {!menuData.hideAdd && (
              <MenuItem
                label={menuData.translate(
                  'activityChartActions.addNewActivity',
                )}
                disabled={!menuData.canEdit}
                onClick={() => handleItemClick(Operations.Add)}
              />
            )}
            {!menuData.addOnly && (
              <MenuItem
                label={menuData.translate(
                  'activityChartActions.changeActivity',
                )}
                disabled={!menuData.canEdit || menuData.activity.type === 4}
                onClick={() => handleItemClick(Operations.Edit)}
              />
            )}
            {!menuData.hideAdd && companyId && driverId && (
              <MenuItem
                label={menuData.translate('countryCode.add-entry')}
                disabled={!menuData.canEdit || !canAddCountryCode}
                onClick={() => handleItemClick(Operations.AddCountryCode)}
              />
            )}
            {!menuData.addOnly && (
              <>
                <MenuDivider />
                <MenuItem
                  label={menuData.translate(
                    `activityChartActions.switch${menuData.activity.isCrew ? 'Off' : 'On'}Crew`,
                  )}
                  disabled={!menuData.canEdit || menuData.activity.type === 4}
                  onClick={() => handleItemClick(Operations.Crew)}
                />
                <MenuItem
                  label={menuData.translate(
                    `activityChartActions.switch${menuData.activity.isOutOfScope ? 'Off' : 'On'}OutOfScope`,
                  )}
                  disabled={!menuData.canEdit || menuData.activity.type === 4}
                  onClick={() => handleItemClick(Operations.OutOfScope)}
                />
                <MenuItem
                  label={menuData.translate(
                    `activityChartActions.switch${menuData.activity.isFerryCrossing ? 'Off' : 'On'}FerryCrossing`,
                  )}
                  disabled={
                    getActivityType(menuData.activity.type) !==
                      ActivityType.REST ||
                    !menuData.canEdit ||
                    menuData.activity.type === 4
                  }
                  onClick={() => handleItemClick(Operations.FerryCrossing)}
                />
                <MenuDivider />
                <MenuItem
                  label={menuData.translate(
                    'activityChartActions.generateAttestation',
                  )}
                  disabled={
                    !menuData.canGenerateAttestation ||
                    menuData.activity.type === 4
                  }
                  onClick={() => handleItemClick(Operations.GenerateAttestation)}
                />
              </>
            )}
          </div>,
          document.body,
        )}
      {countryCodeDateTime && companyId && driverId && (
        <AddCountryCodeModal
          companyId={companyId}
          driverId={driverId}
          defaultDateTime={countryCodeDateTime}
          handleClose={() => setCountryCodeDateTime(null)}
        />
      )}
    </ActivityContextMenuContext.Provider>
  )
}

const MenuItem: React.FC<{
  label: string
  disabled?: boolean
  onClick: () => void
}> = ({ label, disabled, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    className={`custom-root ${disabled ? 'react-contextmenu-item--disabled' : ''}`}
    style={{
      padding: '6px 12px',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      fontSize: 13,
      whiteSpace: 'nowrap',
      userSelect: 'none',
      display: 'block',
      width: '100%',
      textAlign: 'left',
      border: 0,
      background: 'transparent',
    }}
    onClick={(e) => {
      e.stopPropagation()
      if (!disabled) onClick()
    }}
    onMouseEnter={(e) => {
      if (!disabled)
        (e.currentTarget as HTMLElement).style.background = '#f0f0f0'
    }}
    onMouseLeave={(e) => {
      ;(e.currentTarget as HTMLElement).style.background = 'transparent'
    }}
  >
    {label}
  </button>
)

const MenuDivider: React.FC = () => (
  <div
    style={{
      height: 1,
      background: 'var(--ctx-menu-divider, #e0e0e0)',
      margin: '4px 0',
    }}
  />
)
